// Navbar.jsx
import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Transition for full-screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Navbar = ({ ToHome, onSignOut }) => {
  // Avatar menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Manage Opportunities dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Dummy opportunities data
  const [opportunities, setOpportunities] = useState([
    {
      id: 1,
      client: "Walmart Inc.",
      oppId: "SF-WMT-2024-001",
      coverage: "Group Dental",
      stage: "RFP Summarization Complete",
      progress: "1 / 4",
      assignedTo: "amanda.smith@pfg.com",
    },
    {
      id: 2,
      client: "Amazon.com",
      oppId: "SF-AMZ-2024-015",
      coverage: "Vision & Dental Bundle",
      stage: "Completeness Check",
      progress: "2 / 4",
      assignedTo: "rahul.mehta@pfg.com",
    },
    {
      id: 3,
      client: "Microsoft Corporation",
      oppId: "SF-MSFT-2024-008",
      coverage: "Group Medical",
      stage: "Quote Consolidation Complete",
      progress: "4 / 4",
      assignedTo: "julia.lee@pfg.com",
    },
  ]);

  // Dummy email options
  const emailOptions = [
    "amanda.smith@pfg.com",
    "rahul.mehta@pfg.com",
    "julia.lee@pfg.com",
    "ops.team@pfg.com",
  ];

  // Pagination (optional, but keeps dialog clean if list grows)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleManageOpportunities = () => {
    handleMenuClose();
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSignOut = () => {
    handleMenuClose();
    if (onSignOut) {
      onSignOut();
    } else {
      // fallback – you can replace with your own logic
      console.log("Sign out clicked");
    }
  };

  const handleAssignedChange = (id, newEmail) => {
    setOpportunities((prev) =>
      prev.map((opp) =>
        opp.id === id ? { ...opp, assignedTo: newEmail } : opp
      )
    );
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <AppBar position="fixed" sx={{ border: 0, borderRadius: 0 }}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: 0,
            justifyContent: "space-between",
          }}
        >
          {/* Left: Logo + App Name */}
          <Box
            onClick={() => {
              ToHome && ToHome();
            }}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <Box
              component="img"
              src="Principal_Logo_1.png" // <-- replace with your logo path
              alt="logo"
              sx={{ width: 100 }}
            />
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: "white", ml: 1 }}
            >
              RFP Triaging and Quoting Engine
            </Typography>
          </Box>

          {/* Right: User Avatar */}
          <Box>
            <IconButton onClick={handleAvatarClick} size="small" sx={{ ml: 2 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "secondary.main",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                U
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={handleManageOpportunities}>
                Manage Opportunities
              </MenuItem>
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Full Screen Dialog for Opportunities */}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleDialogClose}
        TransitionComponent={Transition}
      >
        {/* Dialog AppBar */}
        <AppBar sx={{ position: "relative", borderRadius: 0 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography
              sx={{ ml: 1, flex: 1, color: "white" }}
              variant="h5"
              component="div"
            >
              Manage Opportunities
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Dialog Content */}
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Review and update ownership of active opportunities.
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Opportunity ID</TableCell>
                  <TableCell>Coverage</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Assigned To</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {opportunities
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((opp) => (
                    <TableRow key={opp.id} hover>
                      <TableCell>{opp.client}</TableCell>
                      <TableCell>{opp.oppId}</TableCell>
                      <TableCell>{opp.coverage}</TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "20px",
                            border: "1px solid #003366",
                            color: "#003366",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {opp.stage}
                        </Box>
                      </TableCell>

                      <TableCell>{opp.progress}</TableCell>

                      <TableCell sx={{ minWidth: 240 }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={opp.assignedTo}
                            onChange={(e) =>
                              handleAssignedChange(opp.id, e.target.value)
                            }
                          >
                            {emailOptions.map((email) => (
                              <MenuItem key={email} value={email}>
                                {email}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={opportunities.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10]}
            />
          </TableContainer>
        </Box>
      </Dialog>
    </>
  );
};

export default Navbar;
